interface SEOConfig {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonical?: string;
  structuredData?: object;
}

export const updateSEOTags = (config: SEOConfig) => {
  // Update page title
  if (config.title) {
    document.title = config.title;
  }

  // Update meta description
  const updateMetaTag = (name: string, content: string) => {
    let tag = document.querySelector(`meta[name="${name}"]`);
    if (tag) {
      tag.setAttribute('content', content);
    } else {
      tag = document.createElement('meta');
      tag.setAttribute('name', name);
      tag.setAttribute('content', content);
      document.head.appendChild(tag);
    }
  };

  // Update Open Graph tags
  const updateOGTag = (property: string, content: string) => {
    let tag = document.querySelector(`meta[property="${property}"]`);
    if (tag) {
      tag.setAttribute('content', content);
    } else {
      tag = document.createElement('meta');
      tag.setAttribute('property', property);
      tag.setAttribute('content', content);
      document.head.appendChild(tag);
    }
  };

  // Update description
  if (config.description) {
    updateMetaTag('description', config.description);
  }

  // Update keywords
  if (config.keywords) {
    updateMetaTag('keywords', config.keywords);
  }

  // Update Open Graph tags
  if (config.ogTitle) {
    updateOGTag('og:title', config.ogTitle);
  }
  if (config.ogDescription) {
    updateOGTag('og:description', config.ogDescription);
  }
  if (config.ogImage) {
    updateOGTag('og:image', config.ogImage);
  }
  if (config.ogUrl) {
    updateOGTag('og:url', config.ogUrl);
  }

  // Update Twitter tags
  if (config.twitterTitle) {
    updateMetaTag('twitter:title', config.twitterTitle);
  }
  if (config.twitterDescription) {
    updateMetaTag('twitter:description', config.twitterDescription);
  }
  if (config.twitterImage) {
    updateMetaTag('twitter:image', config.twitterImage);
  }

  // Update canonical URL
  if (config.canonical) {
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', config.canonical);
    } else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', config.canonical);
      document.head.appendChild(canonical);
    }
  }

  // Add structured data
  if (config.structuredData) {
    // Remove existing structured data for this page
    const existingScript = document.querySelector('script[type="application/ld+json"][data-page-specific]');
    if (existingScript) {
      existingScript.remove();
    }

    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-page-specific', 'true');
    script.textContent = JSON.stringify(config.structuredData);
    document.head.appendChild(script);
  }
};

// Common SEO configurations for different page types
export const seoConfigs = {
  home: {
    title: "Prize Drop - Win Prizes Daily | Free Competitions & Giveaways South Africa",
    description: "Prize Drop is South Africa's premier competition platform! Win prizes daily with exciting competitions, premium contests, and free giveaways. Join thousands winning cash, electronics, and amazing rewards!",
    keywords: "prizedrop, prize drop, win prizes, competitions, contests, daily competitions, giveaways, free prizes, south africa competitions, cash prizes, win money, daily giveaways, competition platform",
    ogTitle: "Prize Drop - Win Prizes Daily | Free Competitions & Giveaways",
    ogDescription: "Prize Drop is South Africa's premier competition platform! Win prizes daily with exciting competitions, premium contests, and free giveaways.",
    ogUrl: "https://prizedrop.co.za",
    canonical: "https://prizedrop.co.za",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Prize Drop - South Africa's Premier Competition Platform",
      "description": "Win daily prizes with exciting competitions and premium contests",
      "url": "https://prizedrop.co.za",
      "mainEntity": {
        "@type": "Organization",
        "name": "Prize Drop",
        "description": "South Africa's leading platform for daily prize competitions, exciting contests, and premium giveaways",
        "url": "https://prizedrop.co.za"
      }
    }
  },
  
  competitions: {
    title: "Prize Competitions | Win Daily Free Giveaways | Prize Drop",
    description: "Browse active prize competitions on Prize Drop! Daily free competitions, premium giveaways, and exclusive contests. Win cash, electronics, holidays, and more!",
    keywords: "prize competitions, daily competitions, win prizes, prizedrop competitions, contest entries, giveaway platform, south africa prizes, premium contests",
    ogTitle: "Prize Competitions | Win Daily Free Giveaways",
    ogDescription: "Browse active prize competitions on Prize Drop! Daily free competitions, premium giveaways, and exclusive contests.",
    ogUrl: "https://prizedrop.co.za/competitions",
    canonical: "https://prizedrop.co.za/competitions",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Prize Competitions",
      "description": "Active prize competitions including exciting contests and premium giveaways",
      "url": "https://prizedrop.co.za/competitions"
    }
  },

  blog: {
    title: "Prize Drop Blog | Competition Tips & Winning Strategies | Free Giveaway Guides",
    description: "Expert competition guides and winning strategies for Prize Drop free competitions and premium giveaways. Learn how to maximize your chances of winning prizes!",
    keywords: "competition guides, winning strategies, contest tips, prize drop blog, competition tips, giveaway strategies, win prizes guide, contest advice",
    ogTitle: "Prize Drop Blog | Competition Tips & Winning Strategies",
    ogDescription: "Expert competition guides and winning strategies for Prize Drop free competitions and premium giveaways.",
    ogUrl: "https://prizedrop.co.za/blog",
    canonical: "https://prizedrop.co.za/blog",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "Prize Drop Blog",
      "description": "Expert guides and strategies for winning prize competitions",
      "url": "https://prizedrop.co.za/blog",
      "publisher": {
        "@type": "Organization",
        "name": "Prize Drop",
        "url": "https://prizedrop.co.za"
      }
    }
  },

  faq: {
    title: "FAQ | Prize Drop Competition Rules | Free Giveaways Help",
    description: "Frequently asked questions about Prize Drop competitions, premium partnerships, free contests, prize claiming, and winning strategies. Get answers now!",
    keywords: "prize drop faq, competition rules, contest help, prize claiming, contest questions, giveaway rules, competition support",
    ogTitle: "FAQ | Prize Drop Competition Rules & Help",
    ogDescription: "Frequently asked questions about Prize Drop competitions, exciting contests, free giveaways, and prize claiming.",
    ogUrl: "https://prizedrop.co.za/faq",
    canonical: "https://prizedrop.co.za/faq",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "name": "Prize Drop FAQ",
      "description": "Frequently asked questions about Prize Drop competitions and exciting contests",
      "url": "https://prizedrop.co.za/faq"
    }
  }
};